import { ActionEnum } from '../enum/action.enum';
import {action} from 'mobx';
import { ActionFunction } from '../interface/action/action-function.type';
import { ISettingsColor, ISettingsFont } from '../interface/settings.interface';
import { FontModel } from '../model/settings/font.model';
import { LogoModel } from '../model/settings/logo.model';
import { ILogo } from '../interface/block/logo.interface';
import { ICookiePolicySettings } from '../interface/cookie-policy-settings.interface';
import * as Cookies from 'js-cookie';
import { COOKIE_POLICY_ACCEPTED } from '../constants';
import { ISpinnerSettings } from '../interface/spinner-settings.interface';
import { IMainMenuSettings } from '../interface/main-menu-settings.interface';
import {AjaxResponse} from 'rxjs';
import {noop} from '../util/action/type/noop-action.util';
import {setGoogleFontsState} from '../util/action/type/set-google-fonts-state.util';
import {ICustomFont} from '../interface/custom-font.interface';
import {IGoogleFont} from '../interface/google-fonts.interface';
import {logDebugEvent} from '../util/debug/log-debug-event.util';
import {shouldSetSamesiteCookie} from '../util/dom/should-set-same-site.util';

export interface IUpdateFontsPayload {
  fonts: ISettingsFont[];
}

export interface IUpdateColorsPayload {
  colors: ISettingsColor[];
}

const updateFonts$: ActionFunction<IUpdateFontsPayload> = (action$, {state}) => {
  return action$
    .type(ActionEnum.SETTINGS_UPDATE_FONTS)
    .end(action((payload: IUpdateFontsPayload) => {
      state.configuration.settings.fonts = payload.fonts.map(font => Object.assign(new FontModel(), font));
    }));
};

export interface IUpdateLogosPayload {
  logos: ILogo[];
}

const updateLogos$: ActionFunction<IUpdateLogosPayload> = (action$, {state}) => {
  return action$
    .type(ActionEnum.SETTINGS_UPDATE_LOGOS)
    .end(action((payload: IUpdateLogosPayload) => {
      state.configuration.settings.logos = payload.logos.map(logo => Object.assign(new LogoModel(), logo));
    }));
};

const updateColors$: ActionFunction<IUpdateColorsPayload> = (action$, {state, logger}) => {
  return action$
    .type(ActionEnum.SETTINGS_UPDATE_COLORS)
    .end(action((payload: IUpdateColorsPayload) => {
      logDebugEvent(logger, 'CMS-SSR SETTINGS_UPDATE_COLORS', {colorsLength: state.configuration.settings.colors.length});
      const settingsColors = state.configuration.settings.colors.filter(color => color.isAdmin);
      state.configuration.settings.colors = settingsColors.concat(payload.colors.map(color => Object.assign({}, color, {isSaved: false})));

      // Remove duplicate color objects
      const unique = {};
      const distinct = [];
      for (const color of state.configuration.settings.colors) {
        if (typeof(unique[color.id]) === 'undefined') {
          distinct.push(color);
        }
        unique[color.id] = 0;
      }
      state.configuration.settings.colors = distinct;
      logDebugEvent(logger, 'CMS-SSR SETTINGS_UPDATE_COLORS unique', {colorsLength: state.configuration.settings.colors.length});
    }));
};

const updateCookiePolicySettings$: ActionFunction<ICookiePolicySettings> = (action$, {state}) => {
  return action$
    .type(ActionEnum.SETTINGS_UPDATE_COOKIE_POLICY_SETTINGS)
    .end(action((payload: ICookiePolicySettings) => {
      state.configuration.settings.cookiePolicy.cookieWarningBackgroundColor = payload.cookieWarningBackgroundColor || 'PrimaryBackgroundColor';
      state.configuration.settings.cookiePolicy.cookieWarningButtonColor = payload.cookieWarningButtonColor || 'PrimaryButtonColor';
      state.configuration.settings.cookiePolicy.cookieWarningText = payload.cookieWarningText || 'I understand';
      state.configuration.settings.cookiePolicy.cookieWarningFontColor = payload.cookieWarningFontColor || 'PrimaryTextColor';
      state.configuration.settings.cookiePolicy.cookieWarningOpacity = payload.cookieWarningOpacity || 0.9;
      state.configuration.settings.cookiePolicy.cookieWarningLink = payload.cookieWarningLink || {} as any;
      state.configuration.settings.cookiePolicy.cookieWarningButtonText = payload.cookieWarningButtonText || 'To get the best experience on this website we use cookies';
    }));
};

const updateSpinnerSettings$: ActionFunction<ISpinnerSettings> = (action$, {state}) => {
  return action$
    .type(ActionEnum.SETTINGS_UPDATE_SPINNER_SETTINGS)
    .end(action((payload: ISpinnerSettings) => {
      state.configuration.settings.spinnerConfig.spinner = payload.spinner || 1;
      state.configuration.settings.spinnerConfig.spinnerColor = payload.spinnerColor || 'PrimaryTextColor';
    }));
};

const updateMainMenuSettings$: ActionFunction<IMainMenuSettings> = (action$, {state}) => {
  return action$
    .type(ActionEnum.SETTINGS_UPDATE_MAIN_MENU_SETTINGS)
    .end(action((payload: IMainMenuSettings) => {
      state.configuration.settings.mainMenu.logo = payload.logo;
      state.configuration.settings.mainMenu.items = payload.items;
    }));
};

const updateButtonSettings$: ActionFunction = (action$, {state}) => {
  return action$
    .type(ActionEnum.SETTINGS_UPDATE_BUTTON_SETTINGS)
    .end(action((payload: number) => {
      state.configuration.settings.buttonThemeId = payload;
    }));
};

const cookiePolicyAccepted$: ActionFunction = (action$, {state}) => {
  return action$
    .type(ActionEnum.COOKIE_POLICY_ACCEPTED)
    .end(action(() => {
      state.configuration.cookiePolicyAccepted = true;
      const cookieOpts = shouldSetSamesiteCookie() ? {sameSite: 'none', secure: true} : {sameSite: false};
      Cookies.set(COOKIE_POLICY_ACCEPTED, '1', cookieOpts);
    }));
};

export const updateGoogleFonts$: ActionFunction = (action$, {state, service}) => {
  return action$
    .type(ActionEnum.HTTP_UPDATE_GOOGLE_FONTS)
    .switchMap(action(() => {
      state.isLoading = true;
      return service.siteService.updateGoogleFonts(state.configuration.googleFonts)
        .map((response: AjaxResponse) => {
          if (response.status === 200) {
            if (response.response && response.response.fonts) {
              return setGoogleFontsState(response.response.fontsFamiliesString, response.response.fonts);
            }
          }
          return noop();
        })
        .finally(action(() => {
          state.isLoading = false;
        }));
    }));
};

export const updateCustomFonts$: ActionFunction = (action$, {state, service}) => {
  return action$
    .type(ActionEnum.HTTP_UPDATE_CUSTOM_FONTS)
    .switchMap(action(() => {
      state.isLoading = true;
      return service.siteService.updateCustomFonts(state.configuration.customFonts)
        .map((response: AjaxResponse) => {
          return noop();
        })
        .finally(action(() => {
          state.isLoading = false;
        }));
    }));
};

export interface ISetGoogleFontsStatePayload {
  fontsFamiliesString: string;
  fonts: IGoogleFont[];
}

export const setGoogleFontsState$: ActionFunction<ISetGoogleFontsStatePayload> = (action$, {state}) => {
  return action$
    .type(ActionEnum.SET_GOOGLE_FONTS_STATE)
    .end(action((payload: ISetGoogleFontsStatePayload) => {
      state.configuration.googleFonts.fontsFamiliesString = payload.fontsFamiliesString;
      state.configuration.googleFonts.fonts = payload.fonts || [];
    }));
};

export interface ISetCustomFontsStatePayload {
  customFonts: ICustomFont[];
}

export const setCustomFontsState$: ActionFunction<ISetCustomFontsStatePayload> = (action$, {state}) => {
  return action$
    .type(ActionEnum.SET_CUSTOM_FONTS_STATE)
    .end(action((payload: ISetCustomFontsStatePayload) => {
      state.configuration.customFonts = payload.customFonts;
    }));
};

export default {
  actions: [
    updateFonts$,
    updateLogos$,
    updateColors$,
    updateCookiePolicySettings$,
    cookiePolicyAccepted$,
    updateSpinnerSettings$,
    updateMainMenuSettings$,
    updateButtonSettings$,
    updateGoogleFonts$,
    setGoogleFontsState$,
    updateCustomFonts$,
    setCustomFontsState$
  ]
};
